import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Download, RotateCcw, Database, ChevronDown, ChevronRight, Play, Wrench, Sparkles,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import JobberImportCard from "./JobberImportCard";

const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const PHASE_LABELS = {
  starting: "Preparing import…",
  customers: "Importing customers…",
  items: "Importing price book items…",
  jobs: "Importing jobs…",
  estimates: "Importing estimates…",
  invoices: "Importing invoices…",
  payments: "Importing payments…",
  commissions: "Enriching jobs with commission data…",
  finalizing: "Finalizing…",
  done: "Done",
};

export default function DataImportTab() {
  const [session, setSession] = useState(null); // active session doc
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [history, setHistory] = useState([]);
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const runFixJobs = async () => {
    setFixing(true);
    setFixResult(null);
    try {
      const res = await axios.post(`${API}/import/workiz/fix-imported-jobs`, {}, getAuth());
      setFixResult(res.data);
      const total = (res.data.jobs_location_assigned || 0) + (res.data.customers_relinked || 0) + (res.data.technicians_linked || 0);
      toast.success(total > 0 ? "Imported jobs fixed!" : "Nothing needed fixing — all imported jobs look good.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fix failed — please try again");
    } finally {
      setFixing(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/import/workiz/sessions`, getAuth());
      setHistory(res.data?.sessions || []);
      return res.data?.sessions || [];
    } catch { return []; }
  };

  // On mount: restore an in-flight session (page refresh must not lose progress)
  useEffect(() => {
    (async () => {
      const sessions = await loadHistory();
      const latest = sessions[0];
      if (latest && ["running", "preview"].includes(latest.status)) {
        try {
          const full = await axios.get(`${API}/import/workiz/${latest.session_id}`, getAuth());
          setSession(full.data);
          if (full.data.status === "running") startPolling(latest.session_id);
        } catch { /* ignore */ }
      }
    })();
    return () => clearInterval(pollRef.current);
  }, []);

  const openSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/import/workiz/${sessionId}`, getAuth());
      setSession(res.data);
      if (res.data.status === "running") startPolling(sessionId);
    } catch {
      toast.error("Could not load import session");
    }
  };

  const startPolling = (sessionId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/import/workiz/${sessionId}`, getAuth());
        setSession(res.data);
        if (["completed", "failed"].includes(res.data?.status)) {
          clearInterval(pollRef.current);
          loadHistory();
          if (res.data.status === "completed") toast.success("Workiz import completed!");
          else toast.error(`Import failed: ${res.data.error || "unknown error"}`);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  const CHUNK_SIZE = 4 * 1024 * 1024; // ~4MB per request — passes any proxy limit

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const uploadId = `up${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
      const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
      let sent = 0;
      const manifest = [];
      for (const file of files) {
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        for (let i = 0; i < totalChunks; i++) {
          const slice = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          const form = new FormData();
          form.append("upload_id", uploadId);
          form.append("filename", file.name);
          form.append("chunk_index", String(i));
          form.append("total_chunks", String(totalChunks));
          form.append("chunk", slice, file.name);
          await axios.post(`${API}/import/workiz/upload-chunk`, form, getAuth());
          sent += slice.size;
          setUploadPct(Math.min(99, Math.round((sent / totalBytes) * 100)));
        }
        manifest.push({ filename: file.name, total_chunks: totalChunks });
      }
      const res = await axios.post(`${API}/import/workiz/upload-complete`, {
        upload_id: uploadId,
        files: manifest,
      }, getAuth());
      setSession(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const startImport = async () => {
    try {
      await axios.post(`${API}/import/workiz/${session.session_id}/commit`, {}, getAuth());
      setSession(s => ({ ...s, status: "running", progress: { phase: "starting" } }));
      startPolling(session.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start import");
    }
  };

  const discard = async () => {
    try {
      await axios.post(`${API}/import/workiz/${session.session_id}/cancel`, {}, getAuth());
    } catch { /* best effort */ }
    setSession(null);
    loadHistory();
  };

  const downloadErrors = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/import/workiz/${sessionId}/errors.csv`, {
        ...getAuth(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `workiz-import-errors-${sessionId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download error report");
    }
  };

  return (
    <div className="space-y-6" data-testid="data-import-tab">
      <div>
        <h3 className="text-lg sm:text-xl font-bold text-foreground">Data Import</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Migrate your business data from another platform. Re-uploading the same export later
          updates your records instead of duplicating them.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-[#FFCC00]" /> Import from Workiz
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Upload the export files from Workiz — Clients, Job reports (one per year), Invoices,
            Estimates, Payments, Items, and Commission reports. Upload a single .zip or select
            multiple files at once. We'll detect each file automatically and show a preview
            before anything is written. Unrecognized formats are mapped automatically with AI.
          </p>
        </CardHeader>
        <CardContent>
          {!session && (
            <div
              className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-[#FFCC00]/60 transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              data-testid="workiz-dropzone"
            >
              <input
                ref={fileRef} type="file" multiple hidden
                accept=".zip,.csv,.xls,.xlsx"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="workiz-file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" />
                  <p className="text-sm text-muted-foreground" data-testid="workiz-upload-progress">
                    Uploading… {uploadPct}%
                  </p>
                  <div className="w-56 h-1.5 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-[#FFCC00] transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-foreground font-medium">
                    Drop your Workiz export here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">.zip, .csv, .xls, .xlsx — no size limit, large files upload in chunks</p>
                </div>
              )}
            </div>
          )}

          {session && session.status === "preview" && (
            <PreviewStep session={session} onStart={startImport} onDiscard={discard} />
          )}

          {session && session.status === "running" && (
            <RunningStep session={session} />
          )}

          {session && ["completed", "failed"].includes(session.status) && (
            <ResultStep
              session={session}
              onDownloadErrors={() => downloadErrors(session.session_id)}
              onReset={() => { setSession(null); loadHistory(); }}
            />
          )}
        </CardContent>
      </Card>

      <JobberImportCard />

      {/* Bulk repair for previously imported data */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#FFCC00]" /> Fix imported jobs
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Repairs jobs from previous Workiz imports: assigns them to your main location,
            re-links them to your real customers (removing duplicate auto-created ones),
            and connects technicians by name. Safe to run multiple times.
          </p>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={runFixJobs}
            disabled={fixing}
            data-testid="workiz-fix-jobs-btn"
          >
            {fixing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}
            {fixing ? "Fixing…" : "Fix imported jobs"}
          </Button>
          {fixResult && (
            <div className="mt-3 text-xs text-muted-foreground space-y-0.5" data-testid="workiz-fix-result">
              <div>✓ {fixResult.jobs_location_assigned} job(s) assigned to main location</div>
              <div>✓ {fixResult.invoices_location_assigned} invoice(s)/estimate(s) assigned to main location</div>
              <div>✓ {fixResult.customers_relinked} job(s) re-linked to real customers</div>
              <div>✓ {fixResult.duplicate_customers_removed} duplicate customer(s) removed</div>
              <div>✓ {fixResult.technicians_linked} technician assignment(s) linked</div>
              {(fixResult.dates_normalized ?? 0) > 0 && (
                <div>✓ {fixResult.dates_normalized} job date value(s) normalized for calendar/timeline views</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Import history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.session_id} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2 flex-wrap" data-testid={`import-history-${h.session_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-foreground">
                      {new Date(h.created_at).toLocaleString()}
                      <span className="text-muted-foreground ml-2 text-xs">
                        {(h.files || []).filter(f => f.file_type).length} file(s)
                      </span>
                    </div>
                    {h.counts && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {h.counts.customers?.created ?? 0} customers · {h.counts.jobs?.created ?? 0} jobs · {h.counts.invoices?.created ?? 0} invoices · {h.counts.errors ?? 0} errors
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={h.status} />
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => openSession(h.session_id)}
                      data-testid={`view-session-${h.session_id}`}
                      title={h.status === "running" ? "View live progress" : "View details"}
                    >
                      {h.status === "running" ? "View progress" : "View"}
                    </Button>
                    {(h.errors_count || 0) > 0 && ["completed", "failed"].includes(h.status) && (
                      <Button size="sm" variant="ghost" onClick={() => downloadErrors(h.session_id)} title="Download error report">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function RunningStep({ session }) {
  const done = session.progress?.done ?? null;
  const total = session.progress?.total ?? null;
  const pct = total ? Math.min(100, Math.round(((done || 0) / total) * 100)) : null;
  return (
    <div className="flex flex-col items-center gap-3 py-8" data-testid="workiz-import-progress">
      <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" />
      <p className="text-sm text-foreground font-medium">
        {PHASE_LABELS[session.progress?.phase] || "Importing…"}
      </p>
      {pct !== null && (
        <>
          <div className="w-72 max-w-full h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-[#FFCC00] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="workiz-progress-bar"
            />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="workiz-progress-counts">
            {(done || 0).toLocaleString()} of {total.toLocaleString()} records · {pct}%
          </p>
        </>
      )}
      {session.progress?.detail && pct === null && (
        <p className="text-xs text-muted-foreground">{session.progress.detail}</p>
      )}
      <p className="text-xs text-muted-foreground text-center">
        You can leave this page or refresh — the import keeps running in the background
        and you can reopen the progress from Import history below.
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    running: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    preview: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    failed: "bg-red-500/15 text-red-500 border-red-500/30",
    cancelled: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  };
  return <Badge variant="outline" className={`${map[status] || map.cancelled} capitalize`}>{status}</Badge>;
}

function PreviewStep({ session, onStart, onDiscard }) {
  const detected = (session.files || []).filter(f => f.file_type);
  const skipped = (session.files || []).filter(f => !f.file_type);
  return (
    <div className="space-y-4" data-testid="workiz-preview">
      <div className="text-sm text-foreground font-medium">
        {detected.length} file(s) recognized — review before importing. Nothing has been written yet.
      </div>
      <div className="space-y-2">
        {detected.map(f => <FilePreviewRow key={f.file_id} file={f} />)}
        {skipped.map(f => (
          <div key={f.file_id} className="border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-foreground">{f.filename}</div>
              <div className="text-xs text-red-400">{f.error}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-2">
        <Button onClick={onStart} className="btn-primary" data-testid="workiz-start-import-btn">
          <Play className="w-4 h-4 mr-1" /> Start import
        </Button>
        <Button variant="outline" onClick={onDiscard} data-testid="workiz-discard-btn">Discard</Button>
      </div>
    </div>
  );
}

function FilePreviewRow({ file }) {
  const [open, setOpen] = useState(false);
  const sample = file.sample || [];
  const cols = sample.length ? Object.keys(sample[0]).slice(0, 6) : [];
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`workiz-file-${file.file_id}`}>
      <button className="w-full flex items-center justify-between gap-2 text-left" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-2 min-w-0">
          <FileSpreadsheet className="w-4 h-4 text-[#FFCC00] shrink-0" />
          <span className="text-sm text-foreground truncate">{file.filename}</span>
          <Badge variant="outline" className="text-xs shrink-0">{file.type_label}</Badge>
          {file.ai_mapped && (
            <Badge
              variant="outline"
              className="bg-violet-500/15 text-violet-400 border-violet-500/30 text-xs shrink-0 gap-1"
              data-testid={`workiz-ai-mapped-${file.file_id}`}
            >
              <Sparkles className="w-3 h-3" /> AI-mapped
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          {file.row_count} rows
          {file.parse_errors > 0 && (
            <span className="text-amber-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {file.parse_errors} skipped
            </span>
          )}
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {open && sample.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          {file.ai_mapped && file.ai_mapping && (
            <div className="mb-3 border border-violet-500/25 bg-violet-500/5 rounded-lg p-2.5" data-testid={`workiz-ai-mapping-${file.file_id}`}>
              <div className="text-xs font-medium text-violet-400 flex items-center gap-1 mb-1.5">
                <Sparkles className="w-3 h-3" /> AI detected this format and mapped the columns:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(file.ai_mapping).map(([field, col]) => (
                  <span key={field} className="text-[11px] px-1.5 py-0.5 rounded bg-theme-surface text-muted-foreground border border-border">
                    <span className="text-foreground">{col}</span> → {field.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
          <table className="text-xs w-full">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                {cols.map(c => <th key={c} className="text-left py-1 pr-4 font-medium whitespace-nowrap">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {sample.map((row, i) => (
                <tr key={i} className="border-b border-border/40">
                  {cols.map(c => (
                    <td key={c} className="py-1 pr-4 text-foreground whitespace-nowrap max-w-[220px] truncate">
                      {String(row[c] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResultStep({ session, onDownloadErrors, onReset }) {
  const c = session.counts || {};
  const failed = session.status === "failed";
  const stats = [
    ["Customers created", c.customers?.created],
    ["Customers merged", c.customers?.merged],
    ["Customers updated", c.customers?.updated],
    ["Customers from job refs", c.customers?.created_from_refs],
    ["Jobs imported", c.jobs?.created],
    ["Jobs updated", c.jobs?.updated],
    ["Estimates imported", c.estimates?.created],
    ["Invoices imported", c.invoices?.created],
    ["Invoices marked paid", c.invoices?.marked_paid],
    ["Payments added", c.payments?.added],
    ["Price book items", c.items?.created],
    ["Inventory items", c.items?.inventory_created],
    ["Jobs enriched (commissions)", c.commissions?.jobs_enriched],
  ].filter(([, v]) => (v ?? 0) > 0);

  return (
    <div className="space-y-4" data-testid="workiz-result">
      <div className="flex items-center gap-2">
        {failed ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
        <span className="text-sm font-medium text-foreground">
          {failed ? "Import failed — created records were rolled back." : "Import completed."}
        </span>
      </div>
      {failed && session.error && <p className="text-xs text-red-400">{session.error}</p>}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {stats.map(([label, value]) => (
            <div key={label} className="border border-border rounded-lg px-3 py-2">
              <div className="text-lg font-bold text-foreground">{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        {(session.errors_count || 0) > 0 && (
          <Button variant="outline" onClick={onDownloadErrors} data-testid="workiz-download-errors-btn">
            <Download className="w-4 h-4 mr-1" /> Download error report ({session.errors_count})
          </Button>
        )}
        <Button variant="outline" onClick={onReset} data-testid="workiz-import-more-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Import more files
        </Button>
      </div>
    </div>
  );
}
